// Toaster — Phase 17 redesign pass.
//
// Thin wrapper around @radix-ui/react-toast (already in deps but never
// wired). Exposes a global `window.toast.success/error/warn/info()`
// helper that any page or fetch handler can call without importing.
//
// Usage:
//   <Toaster />   in App.js root
//   window.toast.success('Saved', 'Plugin published')
//   window.toast.error('Failed', err.message)

import { useEffect, useState, useCallback } from 'react';
import * as Toast from '@radix-ui/react-toast';

const KIND_COLOR = {
  success: { border: '#10b981', icon: '✓' },
  error:   { border: '#ef4444', icon: '✗' },
  warn:    { border: '#f59e0b', icon: '⚠' },
  info:    { border: '#06b6d4', icon: 'ℹ' },
};

let pushFn = null;

export function Toaster() {
  const [items, setItems] = useState([]);

  const push = useCallback((kind, title, desc, opts = {}) => {
    const id = Math.random().toString(36).slice(2);
    setItems(prev => [...prev, { id, kind, title, desc, opts }]);
    return () => setItems(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    pushFn = push;
    window.toast = {
      success: (title, desc, opts) => push('success', title, desc, opts),
      error:   (title, desc, opts) => push('error',   title, desc, { duration: 7000, ...opts }),
      warn:    (title, desc, opts) => push('warn',    title, desc, opts),
      info:    (title, desc, opts) => push('info',    title, desc, opts),
    };
    return () => {
      pushFn = null;
      if (window.toast) delete window.toast;
    };
  }, [push]);

  const dismiss = (id) => setItems(prev => prev.filter(t => t.id !== id));

  return (
    <Toast.Provider swipeDirection="right" duration={4500}>
      {items.map(t => (
        <Toast.Root
          key={t.id}
          open
          onOpenChange={(open) => { if (!open) dismiss(t.id); }}
          duration={t.opts?.duration || 4500}
          className="fixed pointer-events-auto"
          style={{
            background: 'rgba(20, 23, 30, 0.94)',
            backdropFilter: 'blur(24px) saturate(150%)',
            WebkitBackdropFilter: 'blur(24px) saturate(150%)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderLeft: `3px solid ${KIND_COLOR[t.kind].border}`,
            borderRadius: 12,
            padding: '12px 16px',
            color: '#d6d8db',
            fontSize: 13,
            lineHeight: 1.4,
            display: 'flex',
            gap: 10,
            maxWidth: 'min(380px, calc(100vw - 48px))',
            boxShadow: '0 12px 32px -8px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset',
            animation: 'hzToastSlideIn 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <span style={{
            color: KIND_COLOR[t.kind].border,
            fontSize: 14,
            flexShrink: 0,
            marginTop: 1,
          }}>{KIND_COLOR[t.kind].icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Toast.Title style={{ fontWeight: 600 }}>{t.title}</Toast.Title>
            {t.desc && (
              <Toast.Description style={{
                color: '#9aa0a6', fontSize: 12, marginTop: 2,
              }}>{t.desc}</Toast.Description>
            )}
          </div>
          <Toast.Close
            aria-label="Dismiss"
            style={{
              background: 'transparent', border: 0,
              color: '#7a808a', cursor: 'pointer',
              padding: '2px 6px', borderRadius: 4,
              fontSize: 14, lineHeight: 1,
            }}>✕</Toast.Close>
        </Toast.Root>
      ))}
      <Toast.Viewport style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 10,
        maxWidth: 'min(380px, calc(100vw - 48px))',
        outline: 'none',
        listStyle: 'none',
        margin: 0,
        padding: 0,
        pointerEvents: 'none',
      }} />
      <style>{`
        @keyframes hzToastSlideIn {
          0%   { transform: translateX(28px) translateY(8px); opacity: 0; }
          100% { transform: translateX(0)    translateY(0);   opacity: 1; }
        }
      `}</style>
    </Toast.Provider>
  );
}
